'use client';

import React from 'react';

export type AlertStage = 0 | 1 | 2 | 3 | 4 | 5;

interface StageInfo {
  code: string;
  label: string;
  description: string;
  cssVar: string;
}

const STAGES: StageInfo[] = [
  { code: 'S0', label: 'Monitoring',           description: 'All clear — device active, vitals normal',         cssVar: 'var(--s0)' },
  { code: 'S1', label: 'Soft flag',            description: 'Anomaly detected — watching for pattern',          cssVar: 'var(--s1)' },
  { code: 'S2', label: 'Unconfirmed',          description: 'Possible incident — awaiting confirmation',         cssVar: 'var(--s2)' },
  { code: 'S3', label: 'Confirmed',            description: 'Incident confirmed — circle alerted',               cssVar: 'var(--s3)' },
  { code: 'S4', label: 'Escalated',            description: 'Professional responders notified',                  cssVar: 'var(--s4)' },
  { code: 'S5', label: 'Unresponsive',         description: 'No response — presumed emergency',                  cssVar: 'var(--s5)' },
];

interface EscalationLadderProps {
  /** Currently active stage (0–5). Undefined = no stage highlighted. */
  activeStage?: AlertStage;
  /** Show stage code + label below each segment */
  showLabels?: boolean;
  /** Show description tooltip / subtitle for active stage */
  showDescription?: boolean;
  /** Compact mode — thinner bar, no labels */
  compact?: boolean;
  /** Additional class names */
  className?: string;
  /** Called when a segment is clicked */
  onStageClick?: (stage: AlertStage) => void;
}

export default function EscalationLadder({
  activeStage,
  showLabels = true,
  showDescription = false,
  compact = false,
  className = '',
  onStageClick,
}: EscalationLadderProps) {
  const barHeight = compact ? 'h-2' : 'h-3';
  const interactive = !!onStageClick;

  return (
    <div className={`w-full ${className}`} role="group" aria-label="Alert stage severity scale">
      {/* Six-segment bar */}
      <div className={`flex w-full rounded overflow-hidden gap-px ${compact ? '' : 'mb-2'}`}>
        {STAGES.map((stage, idx) => {
          const isActive = activeStage === idx;
          const isPast   = activeStage !== undefined && idx < activeStage;
          const isFuture = activeStage !== undefined && idx > activeStage;

          return (
            <button
              key={stage.code}
              type="button"
              disabled={!interactive}
              onClick={() => onStageClick?.(idx as AlertStage)}
              aria-label={`${stage.code}: ${stage.label}${isActive ? ' (current)' : ''}`}
              aria-pressed={isActive}
              className={[
                'flex-1 relative transition-all duration-300',
                barHeight,
                isActive ? 'stage-transition' : '',
                isFuture ? 'opacity-30' : '',
                isPast   ? 'opacity-60' : '',
                interactive ? 'cursor-pointer hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-1' : 'cursor-default',
              ].filter(Boolean).join(' ')}
              style={{
                backgroundColor: stage.cssVar,
                /* Active segment gets a subtle top highlight */
                boxShadow: isActive ? `0 0 0 2px ${stage.cssVar}, inset 0 -2px 0 rgba(0,0,0,0.2)` : undefined,
              }}
            />
          );
        })}
      </div>

      {/* Labels */}
      {showLabels && !compact && (
        <div className="flex w-full gap-px">
          {STAGES.map((stage, idx) => {
            const isActive = activeStage === idx;
            return (
              <div
                key={`label-${stage.code}`}
                className="flex-1 flex flex-col items-center pt-1"
              >
                <span
                  className="font-condensed text-xs font-semibold leading-none"
                  style={{ color: isActive ? stage.cssVar : 'var(--ink-3)' }}
                >
                  {stage.code}
                </span>
                <span
                  className="text-xs leading-tight text-center mt-0.5 hidden sm:block"
                  style={{ color: isActive ? stage.cssVar : 'var(--ink-3)', fontSize: '0.65rem' }}
                >
                  {stage.label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Active stage description */}
      {showDescription && activeStage !== undefined && (
        <p
          className="mt-3 text-sm"
          style={{ color: STAGES[activeStage].cssVar }}
        >
          <span className="font-condensed font-semibold">{STAGES[activeStage].code} — {STAGES[activeStage].label}:</span>{' '}
          {STAGES[activeStage].description}
        </p>
      )}
    </div>
  );
}

/* Named export for stage metadata — useful for other components */
export { STAGES };
export type { StageInfo };
